import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Store, ChevronRight } from "lucide-react";

import { createOutlet } from "../services/business";

export function OutletDetails() {

    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({

        outletName: "",

        addressLine1: "",
        addressLine2: "",

        city: "",
        state: "",
        pincode: "",

        contactNumber: ""

    });

    function handleChange(event) {

        const { name, value } = event.target;

        setFormData(previous => ({

            ...previous,

            [name]: value

        }));

    }

    async function handleSubmit(event) {

        event.preventDefault();

        if (loading) {

            return;

        }

        try {

            setLoading(true);

            const outletUid = await createOutlet({

                outletName: formData.outletName.trim(),

                addressLine1: formData.addressLine1.trim(),

                addressLine2: formData.addressLine2.trim(),

                city: formData.city.trim(),

                state: formData.state,

                pincode: formData.pincode.trim(),

                contactNumber: formData.contactNumber.trim(),

                isPrimary: true,

            });

            console.log("outletUid in OutletDetails: ", outletUid.outletUid);

            localStorage.setItem(
                "onboardingOutletUid", 
                outletUid.outletUid
            )

            navigate("/choose-integration");

        }

        catch (error) {

            console.error(error);

            setLoading(false);

        }

    }

    return (

        <>

            <header className="setup-header">

                <div className="setup-header-logo">

                    <img
                        src={require("./../imgs/nextgo-logo.png")}
                        alt="NextGo Logo"
                        className="setup-header-logo-icon"
                    />

                    <span className="setup-header-logo-text">
                        Next<span className="blue">Go</span>
                    </span>

                </div>

            </header>

            <main className="business-details-page">

                <div className="business-details-layout">

                    <aside className="setup-sidebar">

                        <div>

                            <span className="setup-badge">
                                Step 2 of 3
                            </span>

                            <h1>
                                Let's set up your business
                            </h1>

                            <p>
                                Create your first outlet. You can always add
                                more outlets from your dashboard later.
                            </p>

                        </div>

                        <div className="setup-progress">

                            <div className="setup-step completed">

                                <div className="setup-step-number">

                                    ✓

                                </div>

                                <div>

                                    <h3>
                                        Business Details
                                    </h3>

                                    <p>
                                        Tell us about your business
                                    </p>

                                </div>

                            </div>

                            <div className="setup-step active">

                                <div className="setup-step-number">

                                    2

                                </div>

                                <div>

                                    <h3>
                                        First Outlet
                                    </h3>

                                    <p>
                                        Add your first outlet
                                    </p>

                                </div>

                            </div>

                            <div className="setup-step">

                                <div className="setup-step-number">

                                    3

                                </div>

                                <div>

                                    <h3>
                                        Integrations
                                    </h3>

                                    <p>
                                        Connect Shopify or Petpooja
                                    </p>

                                </div>

                            </div>

                        </div>

                        <div className="setup-tip">

                            <strong>
                                Note
                            </strong>

                            <p>
                                You will be able to add more outlets and manage
                                them later from the dashboard.
                            </p>

                        </div>

                    </aside>

                    <section className="business-details-card">

                        <form onSubmit={handleSubmit}>

                            <h2>
                                Add your first outlet
                            </h2>

                            <p className="setup-description">
                                This will be the primary outlet for your business.
                            </p>

                            <div className="form-group">

                                <label>
                                    Outlet Name
                                </label>

                                <div className="input-with-icon">

                                    <Store size={18} />

                                    <input
                                        type="text"
                                        name="outletName"
                                        placeholder="e.g. Punjabi Tadka - Main Branch"
                                        value={formData.outletName}
                                        onChange={handleChange}
                                    />

                                </div>

                                <small>
                                    Give your outlet a recognizable name.
                                </small>

                            </div>

                            <div className="form-group">

                                <label>
                                    Address Line 1
                                </label>

                                <input
                                    type="text"
                                    name="addressLine1"
                                    placeholder="Building, Street"
                                    value={formData.addressLine1}
                                    onChange={handleChange}
                                    className="setup-input no-icon"
                                />

                                <small>
                                    Enter your street address.
                                </small>

                            </div>

                            <div className="form-group">

                                <label>
                                    Address Line 2
                                    <span className="optional">
                                        {" "} (Optional)
                                    </span>
                                </label>

                                <input
                                    type="text"
                                    name="addressLine2"
                                    placeholder="Apartment, Floor, Landmark"
                                    value={formData.addressLine2}
                                    onChange={handleChange}
                                    className="setup-input no-icon"
                                />

                            </div>

                            <div className="setup-grid">

                                <div className="form-group">

                                    <label>
                                        City
                                    </label>

                                    <input
                                        type="text"
                                        name="city"
                                        placeholder="e.g. Amritsar"
                                        value={formData.city}
                                        onChange={handleChange}
                                        className="setup-input no-icon"
                                    />

                                </div>

                                <div className="form-group">

                                    <label>
                                        State
                                    </label>

                                    <select
                                        name="state"
                                        value={formData.state}
                                        onChange={handleChange}
                                        className="setup-input no-icon"
                                    >

                                        <option value="">
                                            Select State / Union Territory
                                        </option>

                                        {/* States */}

                                        <option value="Andhra Pradesh">
                                            Andhra Pradesh
                                        </option>

                                        <option value="Arunachal Pradesh">
                                            Arunachal Pradesh
                                        </option>

                                        <option value="Assam">
                                            Assam
                                        </option>

                                        <option value="Bihar">
                                            Bihar
                                        </option>

                                        <option value="Chhattisgarh">
                                            Chhattisgarh
                                        </option>

                                        <option value="Goa">
                                            Goa
                                        </option>

                                        <option value="Gujarat">
                                            Gujarat
                                        </option>

                                        <option value="Haryana">
                                            Haryana
                                        </option>

                                        <option value="Himachal Pradesh">
                                            Himachal Pradesh
                                        </option>

                                        <option value="Jharkhand">
                                            Jharkhand
                                        </option>

                                        <option value="Karnataka">
                                            Karnataka
                                        </option>

                                        <option value="Kerala">
                                            Kerala
                                        </option>

                                        <option value="Madhya Pradesh">
                                            Madhya Pradesh
                                        </option>

                                        <option value="Maharashtra">
                                            Maharashtra
                                        </option>

                                        <option value="Manipur">
                                            Manipur
                                        </option>

                                        <option value="Meghalaya">
                                            Meghalaya
                                        </option>

                                        <option value="Mizoram">
                                            Mizoram
                                        </option>

                                        <option value="Nagaland">
                                            Nagaland
                                        </option>

                                        <option value="Odisha">
                                            Odisha
                                        </option>

                                        <option value="Punjab">
                                            Punjab
                                        </option>

                                        <option value="Rajasthan">
                                            Rajasthan
                                        </option>

                                        <option value="Sikkim">
                                            Sikkim
                                        </option>

                                        <option value="Tamil Nadu">
                                            Tamil Nadu
                                        </option>

                                        <option value="Telangana">
                                            Telangana
                                        </option>

                                        <option value="Tripura">
                                            Tripura
                                        </option>

                                        <option value="Uttar Pradesh">
                                            Uttar Pradesh
                                        </option>

                                        <option value="Uttarakhand">
                                            Uttarakhand
                                        </option>

                                        <option value="West Bengal">
                                            West Bengal
                                        </option>

                                        {/* Union Territories */}

                                        <option value="Andaman and Nicobar Islands">
                                            Andaman and Nicobar Islands
                                        </option>

                                        <option value="Chandigarh">
                                            Chandigarh
                                        </option>

                                        <option value="Dadra and Nagar Haveli and Daman and Diu">
                                            Dadra and Nagar Haveli and Daman and Diu
                                        </option>

                                        <option value="Delhi">
                                            Delhi
                                        </option>

                                        <option value="Jammu and Kashmir">
                                            Jammu and Kashmir
                                        </option>

                                        <option value="Ladakh">
                                            Ladakh
                                        </option>

                                        <option value="Lakshadweep">
                                            Lakshadweep
                                        </option>

                                        <option value="Puducherry">
                                            Puducherry
                                        </option>

                                    </select>
                                </div>

                            </div>

                            <div className="setup-grid">

                                <div className="form-group">

                                    <label>
                                        PIN Code
                                    </label>

                                    <input
                                        type="text"
                                        name="pincode"
                                        placeholder="e.g. 143001"
                                        value={formData.pincode}
                                        onChange={handleChange}
                                        className="setup-input no-icon"
                                    />

                                </div>

                                <div className="form-group">

                                    <label>
                                        Contact Number
                                        <span className="optional">
                                            {" "} (Optional)
                                        </span>
                                    </label>

                                    <input
                                        type="text"
                                        name="contactNumber"
                                        placeholder="e.g. +91 98765 43210"
                                        value={formData.contactNumber}
                                        onChange={handleChange}
                                        className="setup-input no-icon"
                                    />

                                    <small>
                                        We may use this to contact your outlet.
                                    </small>

                                </div>

                            </div>

                            <div className="setup-actions">

                                {/* <button
                                    type="button"
                                    className="secondary-button"
                                    onClick={() => navigate("/setup/business")}
                                >
                                    Back
                                </button> */}

                                <button
                                    type="submit"
                                    className="primary-button"
                                    disabled={loading}
                                >

                                    {
                                        loading ?

                                            "Creating..."

                                            :

                                            <>

                                                Continue

                                                <ChevronRight
                                                    size={18}
                                                    style={{
                                                        marginLeft: 6
                                                    }}
                                                />

                                            </>
                                    }

                                </button>

                            </div>

                        </form>

                    </section>

                </div>

            </main>

        </>

    );

}